/**
 * CADERChargesAnalysis - Detailed California DER Charges Breakdown
 *
 * Displays comprehensive analysis of:
 * - Departing Load Charges (PCIA + Non-Bypassable Charges)
 * - Standby Reservation Charges
 * - Monthly breakdown and calculation methodology
 */

import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    Grid,
    Tooltip,
} from '@mui/material';
import {
    ElectricBolt,
    Info,
    CheckCircle,
    Warning,
    TrendingDown,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

// Utility display names
const UTILITY_NAMES = {
    PGE: 'Pacific Gas & Electric',
    SCE: 'Southern California Edison',
    SDGE: 'San Diego Gas & Electric',
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format currency
 */
const formatCurrency = (value, decimals = 0) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(value);
};

/**
 * Format number with commas
 */
const formatNumber = (value, decimals = 0) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(value);
};

/**
 * Format rate ($/kWh)
 */
const formatRate = (value) => {
    if (value === null || value === undefined) return '-';
    return `$${value.toFixed(5)}/kWh`;
};

/**
 * Section Card component - uses theme-aware colors
 */
function SectionCard({ title, icon, children }) {
    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {icon}
                    <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: vortxTypography.heading, color: 'text.primary' }}>
                        {title}
                    </Typography>
                </Box>
                {children}
            </CardContent>
        </Card>
    );
}

/**
 * Main CA DER Charges Analysis Component
 */
export default function CADERChargesAnalysis({ monthlyPerformanceData, caDerConfig }) {
    // Extract CA DER charges data
    const caDerCharges = monthlyPerformanceData?.ca_der_charges || {};
    const monthlyDerCharges = monthlyPerformanceData?.monthly_der_charges || [];
    const departingLoadCharges = caDerCharges.departing_load_charges || {};
    const standbyCharges = caDerCharges.standby_charges || {};

    // Utility MUST come from tariff - no fallback to geographic detection
    const utility = caDerCharges.utility || caDerConfig?.calculated?.effective_utility;
    const utilityName = utility ? (UTILITY_NAMES[utility] || utility) : null;

    // If no utility, show error - tariff selection is required
    if (!utility) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                CA DER charges require a tariff with utility information. Please select a tariff to calculate charges.
            </Alert>
        );
    }

    // Calculate totals
    const totalDepartingLoad = departingLoadCharges.total_annual || 0;
    const totalStandby = standbyCharges.total_annual || 0;
    const totalAnnual = caDerCharges.total_annual_ca_der_charges || (totalDepartingLoad + totalStandby);
    const avoidedStandby = standbyCharges.avoided_cost_annual || 0;

    // Check if we have meaningful data
    const hasDepartingLoad = totalDepartingLoad > 0 || (departingLoadCharges.components && Object.keys(departingLoadCharges.components).length > 0);
    const hasStandby = totalStandby > 0 || standbyCharges.suspension_applied;

    if (!hasDepartingLoad && !hasStandby) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                No CA DER charges apply to this project, or charges have not been calculated yet.
            </Alert>
        );
    }

    return (
        <Box>
            {/* Header with Utility */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <ElectricBolt sx={{ color: vortxColors.gold, fontSize: 32 }} />
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: vortxTypography.heading, color: 'text.primary' }}>
                        California DER Charges Analysis
                    </Typography>
                    <Chip
                        label={utilityName}
                        size="small"
                        sx={{
                            backgroundColor: alpha(vortxColors.gold, 0.15),
                            color: vortxColors.gold,
                            fontWeight: 600,
                            mt: 0.5,
                        }}
                    />
                </Box>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha(vortxColors.gold, 0.1), border: `1px solid ${alpha(vortxColors.gold, 0.3)}` }}>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontFamily: vortxTypography.body }}>
                            Total Annual DER Charges
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: vortxColors.gold, fontFamily: vortxTypography.heading }}>
                            {formatCurrency(totalAnnual)}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontFamily: vortxTypography.body }}>
                            Departing Load Charges
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: vortxColors.gold, fontFamily: vortxTypography.heading }}>
                            {formatCurrency(totalDepartingLoad)}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, textAlign: 'center', border: totalStandby > 0 ? `1px solid #d32f2f` : `1px solid #2e7d32` }}>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontFamily: vortxTypography.body }}>
                            Standby Charges
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: totalStandby > 0 ? '#ef5350' : '#66bb6a', fontFamily: vortxTypography.heading }}>
                            {formatCurrency(totalStandby)}
                        </Typography>
                        {avoidedStandby > 0 && (
                            <Typography variant="caption" sx={{ color: '#66bb6a' }}>
                                ({formatCurrency(avoidedStandby)} avoided via suspension)
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Departing Load Charges Section */}
            {hasDepartingLoad && (
                <SectionCard
                    title="Departing Load Charges (Non-Bypassable Charges)"
                    icon={<Warning sx={{ color: vortxColors.gold }} />}
                >
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>NGOM Method:</strong> Departing Load = Generation - Exports (self-consumed energy).
                            These charges apply to energy you generate and consume on-site, bypassing utility delivery.
                        </Typography>
                    </Alert>

                    {/* Volume and Rate Summary */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="overline" sx={{ color: 'text.secondary', fontFamily: vortxTypography.body }}>
                                    Departed Load Volume
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {formatNumber(departingLoadCharges.total_departed_volume_kwh)} kWh
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="overline" sx={{ color: 'text.secondary', fontFamily: vortxTypography.body }}>
                                    Effective Rate
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {formatRate(departingLoadCharges.effective_rate_per_kwh)}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 2, bgcolor: alpha(vortxColors.gold, 0.1) }}>
                                <Typography variant="overline" sx={{ color: 'text.secondary', fontFamily: vortxTypography.body }}>
                                    Annual Total
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 600, color: vortxColors.gold }}>
                                    {formatCurrency(totalDepartingLoad)}
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Component Breakdown */}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontFamily: vortxTypography.heading, color: 'text.primary' }}>
                        Charge Components
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Component</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Rate ($/kWh)</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Annual Cost</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {departingLoadCharges.components && Object.entries(departingLoadCharges.components).map(([key, component]) => (
                                    <TableRow key={key}>
                                        <TableCell>
                                            <Tooltip title={getComponentDescription(key)}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {getComponentLabel(key)}
                                                    {key === 'pcia' && component.vintage_year && (
                                                        <Chip
                                                            label={`${component.vintage_year} Vintage`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: alpha(vortxColors.gold, 0.15),
                                                                color: vortxColors.gold,
                                                                fontSize: '0.7rem'
                                                            }}
                                                        />
                                                    )}
                                                    <Info sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">{formatRate(component.rate_per_kwh)}</TableCell>
                                        <TableCell align="right">{formatCurrency(component.annual)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: alpha(vortxColors.gold, 0.1) }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                        {formatRate(departingLoadCharges.effective_rate_per_kwh)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: vortxColors.gold }}>
                                        {formatCurrency(totalDepartingLoad)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </SectionCard>
            )}

            {/* Standby Charges Section */}
            {hasStandby && (
                <SectionCard
                    title="Standby Reservation Charges"
                    icon={standbyCharges.suspension_applied ?
                        <CheckCircle sx={{ color: '#66bb6a' }} /> :
                        <TrendingDown sx={{ color: '#ef5350' }} />
                    }
                >
                    {/* Suspension Status */}
                    {standbyCharges.suspension_applied ? (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>D.21-07-011 Suspension Applied!</strong> Your system qualifies for standby charge suspension
                                as a Title 17-compliant clean generation facility. You are avoiding{' '}
                                <strong>{formatCurrency(avoidedStandby)}/year</strong> in standby charges.
                            </Typography>
                        </Alert>
                    ) : (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Standby charges apply.</strong> {standbyCharges.suspension_reason ||
                                    'System does not meet D.21-07-011 eligibility criteria.'}
                            </Typography>
                        </Alert>
                    )}

                    {/* Standby Calculation Details */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={3}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="overline" sx={{ color: 'text.secondary', fontFamily: vortxTypography.body }}>
                                    Reservation Capacity
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {formatNumber(standbyCharges.reservation_capacity_kw)} kW
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    min(Nameplate, Peak Load)
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="overline" sx={{ color: 'text.secondary', fontFamily: vortxTypography.body }}>
                                    Billing kW
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {formatNumber(standbyCharges.billing_kw)} kW
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    × {((standbyCharges.billing_multiplier || 1) * 100).toFixed(0)}% multiplier
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="overline" sx={{ color: 'text.secondary', fontFamily: vortxTypography.body }}>
                                    Monthly Rate
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    ${(standbyCharges.rate_per_kw_month || 0).toFixed(2)}/kW
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {standbyCharges.voltage_level || 'secondary'} voltage
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Paper sx={{
                                p: 2,
                                bgcolor: standbyCharges.suspension_applied ? alpha('#66bb6a', 0.1) : alpha('#ef5350', 0.1),
                                border: `1px solid ${standbyCharges.suspension_applied ? '#66bb6a' : '#ef5350'}`
                            }}>
                                <Typography variant="overline" sx={{ color: 'text.secondary', fontFamily: vortxTypography.body }}>
                                    Annual Standby
                                </Typography>
                                <Typography variant="h6" sx={{
                                    fontWeight: 600,
                                    color: standbyCharges.suspension_applied ? '#66bb6a' : '#ef5350'
                                }}>
                                    {formatCurrency(totalStandby)}
                                </Typography>
                                {standbyCharges.suspension_applied && (
                                    <Typography variant="caption" sx={{ color: '#66bb6a' }}>
                                        (Suspended)
                                    </Typography>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Calculation Formula */}
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontFamily: vortxTypography.heading, color: 'text.primary' }}>
                            Calculation Formula
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: vortxTypography.mono, color: 'text.primary' }}>
                            Billing kW = Reservation Capacity × Billing Multiplier
                            <br />
                            Billing kW = {formatNumber(standbyCharges.reservation_capacity_kw)} kW × {((standbyCharges.billing_multiplier || 1) * 100).toFixed(0)}% = {formatNumber(standbyCharges.billing_kw)} kW
                            <br /><br />
                            Monthly Cost = Billing kW × Rate
                            <br />
                            Monthly Cost = {formatNumber(standbyCharges.billing_kw)} kW × ${(standbyCharges.rate_per_kw_month || 0).toFixed(2)}/kW = {formatCurrency(standbyCharges.monthly_cost_per_month || (standbyCharges.billing_kw * standbyCharges.rate_per_kw_month))}
                            <br /><br />
                            Annual Cost = Monthly Cost × 12 {standbyCharges.suspension_applied ? '× 0 (Suspended)' : ''} = <span style={{ color: vortxColors.gold, fontWeight: 600 }}>{formatCurrency(totalStandby)}</span>
                        </Typography>
                    </Paper>
                </SectionCard>
            )}

            {/* Monthly Breakdown */}
            {monthlyDerCharges.length > 0 && (
                <SectionCard title="Monthly Charges Breakdown" icon={<ElectricBolt sx={{ color: vortxColors.gold }} />}>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Departing Load</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Standby</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {monthlyDerCharges.map((month, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{MONTH_NAMES[index]}</TableCell>
                                        <TableCell align="right">{formatCurrency(month.departing_load_cost)}</TableCell>
                                        <TableCell align="right">{formatCurrency(month.standby_cost)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                                            {formatCurrency(month.total_der_charges)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: alpha(vortxColors.gold, 0.1) }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Annual Total</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                        {formatCurrency(totalDepartingLoad)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                        {formatCurrency(totalStandby)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: vortxColors.gold }}>
                                        {formatCurrency(totalAnnual)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </SectionCard>
            )}

            {/* Methodology Notes */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontFamily: vortxTypography.heading, color: 'text.primary' }}>
                    <Info sx={{ fontSize: 18, color: vortxColors.gold }} /> Methodology Notes
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    • <strong>Departing Load Charges</strong> use the NGOM (Net Generation Output Method) to calculate self-consumed generation.
                    <br />
                    • <strong>Standby Charges</strong> are based on reservation capacity using utility-specific billing multipliers (PG&E: 85%, SCE/SDG&E: 100%).
                    <br />
                    • <strong>D.21-07-011 Suspension</strong> applies to Title 17-compliant (100% clean) generation facilities meeting availability requirements.
                    <br />
                    • Rates are based on current utility tariff schedules and may be subject to change.
                </Typography>
            </Paper>
        </Box>
    );
}

/**
 * Get human-readable label for charge component
 */
function getComponentLabel(key) {
    const labels = {
        pcia: 'PCIA (Power Charge Indifference Adjustment)',
        nuclear_decom: 'Nuclear Decommissioning',
        wildfire_fund: 'DWR Wildfire Fund',
        ppp: 'Public Purpose Programs',
        ctc: 'Competition Transition Charge',
    };
    return labels[key] || key;
}

/**
 * Get description for charge component
 */
function getComponentDescription(key) {
    const descriptions = {
        pcia: 'Recovers costs from utility generation contracts. Rate varies by vintage year.',
        nuclear_decom: 'Funds decommissioning of utility nuclear plants.',
        wildfire_fund: 'DWR bond charges for wildfire liability coverage.',
        ppp: 'Funds low-income assistance, energy efficiency, and renewable programs.',
        ctc: 'Recovers stranded costs from utility restructuring (typically $0).',
    };
    return descriptions[key] || '';
}
